import { FormEvent, useState } from 'react';
import { dealerErrorCode, dealerErrorDetail, dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { DealerRefund } from '../../types/dealers';
import TypedConfirmDialog from '../TypedConfirmDialog';
import { describedBy, FormField } from '../promotions/PromoFormParts';
import { aed, amountError } from './dealerFormat';

/** Hundredths of an AED, to compare two 2-dp inputs exactly. */
const fils = (s: string) => Math.round(Number(s.trim()) * 100);

interface RevokeAfterPaidDialogProps {
  dealerId: string;
  /** A LIVE buyer credit or an ACTIVE dealer lot. */
  target: { kind: 'credit' | 'lot'; id: string; label: string; remainingAed: string };
  token: string;
  onClose: () => void;
  onRevoked: (refund: DealerRefund) => void;
  onFeatureOff: () => void;
}

/**
 * Spec 085 §3 — revoke paid credit (super admin, exceptional). Only the unreserved remaining X can be
 * revoked; the cash refund C to the dealer must be ≤ X (blank = the server's default, X less the
 * dealer's discount). Issues a credit note (DCN) and a dealer refund to mark paid later. The form
 * collects the figures, then `TypedConfirmDialog` confirms.
 */
export default function RevokeAfterPaidDialog({ dealerId, target, token, onClose, onRevoked, onFeatureOff }: RevokeAfterPaidDialogProps) {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [refund, setRefund] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!reason.trim()) e.reason = 'Enter the reason';
    const x = amount.trim() ? amount : target.remainingAed;
    if (amount.trim()) {
      const a = amountError(amount);
      if (a) e.amountAed = a;
      else if (fils(amount) > fils(target.remainingAed)) e.amountAed = `At most the remaining ${aed(target.remainingAed)}`;
    }
    if (refund.trim()) {
      const r = amountError(refund, { allowZero: true });
      if (r) e.refundToDealerAed = r;
      else if (!e.amountAed && fils(refund) > fils(x)) e.refundToDealerAed = `The cash refund cannot exceed the revoked amount (${aed(x)})`;
    }
    return e;
  };

  const next = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const local = validate();
    setErrors(local);
    if (Object.keys(local).length === 0) setConfirming(true);
  };

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        reason: reason.trim(),
        ...(amount.trim() ? { amountAed: amount.trim() } : {}),
        ...(refund.trim() ? { refundToDealerAed: refund.trim() } : {}),
      };
      const saved = target.kind === 'credit'
        ? await dealersApi.revokeCreditAfterPaid(dealerId, target.id, body, token)
        : await dealersApi.revokeLotAfterPaid(dealerId, target.id, body, token);
      onRevoked(saved);
    } catch (err) {
      setConfirming(false);
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      const code = dealerErrorCode(err);
      if (code === 'AMOUNT_EXCEEDS_REMAINING') {
        const rem = dealerErrorDetail(err, 'remainingAed');
        setErrors({ amountAed: rem ? `At most the unreserved remaining ${aed(rem)}` : dealerErrorMessage(err) });
      } else if (code === 'REFUND_EXCEEDS_REVOKED') {
        setErrors({ refundToDealerAed: dealerErrorMessage(err) });
      } else {
        setFormError(dealerErrorMessage(err, 'Failed to revoke'));
      }
    } finally {
      setSaving(false);
    }
  };

  const x = amount.trim() || target.remainingAed;

  return (
    <>
      <div className="admin-modal-overlay">
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Revoke paid ${target.kind}`} style={{ maxWidth: '520px', width: '100%' }}>
          <div className="admin-modal-header">
            <h3 style={{ margin: 0 }}>Revoke paid {target.kind === 'credit' ? 'credit' : 'balance'}</h3>
            <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
          </div>
          <form onSubmit={next} noValidate>
            <div className="admin-modal-body">
              <div className="admin-info-banner warning" role="note">
                Exceptional, super admin only. Only the unreserved remaining ({aed(target.remainingAed)}) can be revoked.
                A credit note is issued and the cash refund is owed to the dealer.
              </div>
              {formError && <div className="admin-info-banner danger" role="alert" data-testid="revoke-after-paid-error">{formError}</div>}
              <FormField id="rap-reason" label="Reason" error={errors.reason}>
                <textarea id="rap-reason" className="admin-form-input" rows={3} maxLength={500} value={reason}
                  {...describedBy('rap-reason', errors.reason)} onChange={(e) => setReason(e.target.value)} />
              </FormField>
              <FormField id="rap-amount" label="Amount to revoke (AED)" error={errors.amountAed} hint={`Blank = the whole remaining, ${aed(target.remainingAed)}.`}>
                <input id="rap-amount" className="admin-form-input" inputMode="decimal" value={amount}
                  {...describedBy('rap-amount', errors.amountAed, true)} onChange={(e) => setAmount(e.target.value)} />
              </FormField>
              <FormField id="rap-refund" label="Cash refund to the dealer (AED)" error={errors.refundToDealerAed}
                hint="Blank = the revoked amount less the dealer's discount at sale. Never more than the revoked amount.">
                <input id="rap-refund" className="admin-form-input" inputMode="decimal" value={refund}
                  {...describedBy('rap-refund', errors.refundToDealerAed, true)} onChange={(e) => setRefund(e.target.value)} />
              </FormField>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="admin-btn admin-btn-danger" disabled={saving}>Continue</button>
            </div>
          </form>
        </div>
      </div>
      {confirming && (
        <TypedConfirmDialog
          title={`Revoke ${aed(x)} of ${target.label}?`}
          message={`Revokes ${aed(x)} and owes the dealer ${refund.trim() ? aed(refund) : 'the default refund'}. This cannot be undone.`}
          confirmPhrase="REVOKE"
          confirmLabel={saving ? 'Revoking…' : 'Revoke'}
          danger
          onConfirm={confirm}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
